import React, { useEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';

import { TextInputElement } from '@editor/components/TextInput/types';
import { UserProfile } from '@repo/domain/user';
import { withEventScope } from '@repo/event-processor';
import { themeStyle } from '@repo/ui/theming';

import { HeaderSearchPortal } from 'components/Layout/LoggedInUserLayout/Header/HeaderSearchPortal';
import { useHeaderSearch } from 'components/Layout/Search/HeaderSearchProvider';
import { buildPublicProfilePath } from 'global/urlUtils';
import useSearch from 'global/utils/useSearch';
import useWindowSize from 'global/utils/useWindowSize';

import Search from '../../../Search/Search';
import { commonInputTheme } from '../HeaderSection/config';

import { SearchWrapper } from './styles';
import { SearchSectionProps } from './types';

const SearchSection: React.FC<SearchSectionProps> = ({ setFilters }) => {
  const router = useRouter();
  const { onSearch, resetQuery, query, tags } = useSearch();
  const { isHeaderRendered, isMobileSearchActive, setIsMobileSearchActive } =
    useHeaderSearch();
  const { isMobile } = useWindowSize();

  const searchInputRef = useRef<TextInputElement>(null);

  // Focus the search input when the search is opened
  useEffect(() => {
    if (isMobileSearchActive) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isMobileSearchActive]);

  const handleOnSearch = (query: string) => {
    setIsMobileSearchActive(false);
    onSearch(query);
    setFilters({});
  };

  const handleResetQuery = () => {
    setIsMobileSearchActive(false);
    resetQuery(false);
    setFilters({});
  };

  const handleSelectUser = (user: UserProfile) => {
    setIsMobileSearchActive(false);
    router.push(buildPublicProfilePath(user.slug));
  };

  const inputTheme = {
    ...commonInputTheme,
    boxShadow: 'none',
    width: '328px',
    height: '40px',
    fontSize: themeStyle.fontSize14,
    icon: {
      height: 'var(--spacing-12)',
      color: 'var(--color-icon-secondary)',
      colorActive: 'var(--color-icon-secondary)',
    },
  };

  const mobileInputTheme = {
    ...commonInputTheme,
    boxShadow: 'none',
    width: '100%',
    height: '40px',
    fontSize: themeStyle.fontSize12,
  };

  const searchComponent = (
    <Search
      showLeftIcon
      dataTestId="design-posts--search"
      enableUserSearch={false}
      inputTheme={inputTheme}
      placeholder="Search for templates"
      query={query}
      resetQuery={handleResetQuery}
      tags={tags}
      onCommit={handleOnSearch}
      onSelectOption={handleOnSearch}
      onSelectUser={handleSelectUser}
    />
  );

  const mobileSearchComponent = (
    <Search
      showRightIcon
      dataTestId="design-posts--search"
      enableUserSearch={false}
      inputRef={searchInputRef}
      inputTheme={mobileInputTheme}
      placeholder="Search for templates"
      query={query}
      resetQuery={handleResetQuery}
      tags={tags}
      onCommit={handleOnSearch}
      onSelectOption={handleOnSearch}
      onSelectUser={handleSelectUser}
    />
  );

  if (isHeaderRendered) {
    return (
      <HeaderSearchPortal>
        {isMobile ? mobileSearchComponent : searchComponent}
      </HeaderSearchPortal>
    );
  }

  return <SearchWrapper>{searchComponent}</SearchWrapper>;
};

export default withEventScope(SearchSection, { feature: 'designsListSearch' });
